import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './Modal.scss';

export default function Modal({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  size = 'medium',
  /** When set, modal content is positioned above this rect (e.g. above FAB). { bottom, right } from getBoundingClientRect() */
  anchorRect = null,
}) {
  const modalRef = useRef(null);

  // ESC key handler
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Focus trap
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      const handleTab = (e) => {
        if (e.key === 'Tab') {
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      };

      document.addEventListener('keydown', handleTab);
      firstElement?.focus();

      return () => {
        document.removeEventListener('keydown', handleTab);
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const overlayClass = anchorRect ? 'modal-overlay modal-overlay-anchored' : 'modal-overlay';
  // Bubble directly above the button. getBoundingClientRect().right = px from viewport left;
  // CSS "right" = distance from viewport right edge, so right = viewportWidth - anchorRect.right
  const contentStyle = anchorRect ? (() => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 400;
    const h = typeof window !== 'undefined' ? window.innerHeight : 600;
    const bottomPx = h - anchorRect.top + 16;
    const rightPx = w - (anchorRect.right ?? w - 24);
    return {
      position: 'fixed',
      bottom: bottomPx,
      right: rightPx,
      left: 'auto',
      maxWidth: 'min(380px, calc(100vw - 32px))',
      maxHeight: 'min(85vh, 560px)',
    };
  })() : undefined;

  const modalContent = (
    <div 
      className={overlayClass}
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div 
        ref={modalRef}
        className={`modal-content modal-${size} ${anchorRect ? 'modal-anchored modal-bubble' : ''}`}
        style={contentStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        {anchorRect && <div className="modal-bubble-tail" aria-hidden />}
        <div className="modal-header">
          <h2 id="modal-title" className="modal-title">{title}</h2>
          <button 
            className="modal-close" 
            onClick={onClose}
            aria-label="Sulje"
          >
            <i className="fas fa-times" />
          </button>
        </div>
        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>
  );

  // Render to portal (body)
  return ReactDOM.createPortal(
    modalContent,
    document.body
  );
}

